import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PageLoader } from '../../components/LoadingSpinner';
import { api } from '../../shared/api';
import { getContrastColor, COLOR_PRIMARY, COLOR_SUCCESS, COLOR_WARNING, COLOR_DANGER, COLOR_SECONDARY } from '../../shared/colorUtils';
import Card from '../../components/Card';
import { useCurrency } from '../../shared/CurrencyContext';
import { useQuery } from '@tanstack/react-query';

const ContractStatistics = () => {
  const { t } = useTranslation();
  const { formatCurrency, currency, currencies, convertAmount } = useCurrency();
  const [timeRange, setTimeRange] = useState('all'); // all, year, month, week
  const [displayCurrency, setDisplayCurrency] = useState(null); // Currency for display

  useEffect(() => {
    // Initialize display currency with default currency when it loads
    if (currency && !displayCurrency) {
      setDisplayCurrency({
        code: currency.code,
        name: currency.name,
        symbol: currency.symbol,
        exchange_rate: parseFloat(currency.exchange_rate)
      });
    }
  }, [currency]);

  const { data: statistics, isLoading: loading, isError } = useQuery({
    queryKey: ['contract-statistics', timeRange, displayCurrency?.code],
    queryFn: async () => {
      const [statsRes, contractsRes] = await Promise.all([
        api.get('contracts/api/contracts/stats/'),
        api.get('contracts/api/contracts/')
      ]);

      const stats = statsRes.data || statsRes;
      const contracts = contractsRes.data || contractsRes;

      const now = new Date();
      const currentYear = now.getFullYear();
      const currentMonth = now.getMonth();
      const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

      const filteredContracts = contracts.filter(contract => {
        const createdAt = new Date(contract.created_at);
        switch (timeRange) {
          case 'year':  return createdAt.getFullYear() === currentYear;
          case 'month': return createdAt.getFullYear() === currentYear && createdAt.getMonth() === currentMonth;
          case 'week':  return createdAt >= oneWeekAgo;
          default:      return true;
        }
      });

      const convertContractBudget = (contract) => {
        const budgetAmount = parseFloat(contract.budget) || 0;
        const contractCurrency = contract.currency_detail || currency;
        if (displayCurrency && contractCurrency.code !== displayCurrency.code) {
          return convertAmount(budgetAmount, contractCurrency, displayCurrency);
        }
        return budgetAmount;
      };

      const totalBudget = filteredContracts.reduce((sum, c) => sum + convertContractBudget(c), 0);
      const avgBudget = filteredContracts.length > 0 ? totalBudget / filteredContracts.length : 0;

      const statusDistribution = filteredContracts.reduce((acc, contract) => {
        const status = contract.status_name || 'Unknown';
        acc[status] = (acc[status] || 0) + 1;
        return acc;
      }, {});

      const monthlyTrend = [];
      for (let i = 11; i >= 0; i--) {
        const date = new Date();
        date.setMonth(date.getMonth() - i);
        const count = contracts.filter(contract => {
          const contractDate = new Date(contract.created_at);
          return contractDate.getFullYear() === date.getFullYear() &&
                 contractDate.getMonth() === date.getMonth();
        }).length;
        monthlyTrend.push({
          month: date.toLocaleDateString('cs-CZ', { month: 'short', year: 'numeric' }),
          count
        });
      }

      const customerStats = filteredContracts.reduce((acc, contract) => {
        const customer = contract.customer_name || 'No Customer';
        if (!acc[customer]) acc[customer] = { count: 0, totalBudget: 0 };
        acc[customer].count += 1;
        acc[customer].totalBudget += convertContractBudget(contract);
        return acc;
      }, {});

      const topCustomers = Object.entries(customerStats)
        .map(([name, data]) => ({ name, ...data }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 5);

      const lockedStats = filteredContracts.reduce((acc, contract) => {
        if (contract.locked) acc.locked += 1;
        else acc.unlocked += 1;
        return acc;
      }, { locked: 0, unlocked: 0 });

      return {
        ...stats,
        filteredContracts: filteredContracts.length,
        totalBudget,
        avgBudget,
        statusDistribution,
        monthlyTrend,
        topCustomers,
        lockedStats
      };
    },
    enabled: !!currency,
  });
  const error = isError ? 'Failed to load statistics. Please try again later.' : null;

  const getStatusHex = (status) => {
    switch (status?.toLowerCase()) {
      case 'aktivní':
      case 'active':    return COLOR_SUCCESS;
      case 'plánováno':
      case 'planned':   return COLOR_WARNING;
      case 'dokončeno':
      case 'completed': return COLOR_PRIMARY;
      case 'zrušeno':
      case 'cancelled': return COLOR_DANGER;
      default:          return COLOR_SECONDARY;
    }
  };

  if (loading) {
    return <PageLoader />;
  }

  return (
    <div className="page-wrapper">
      <div className="page-header d-print-none">
        <div className="container-xl">
          <div className="row g-2 align-items-center">
            <div className="col">
              <div className="page-pretitle">
                <Link to="/contracts" className="text-muted">
                  {t('contracts.title')}
                </Link>
              </div>
              <h2 className="page-title">
                {t('contracts.statistics')}
              </h2>
            </div>
            <div className="col-auto ms-auto d-print-none">
              <div className="btn-list">
                <div className="dropdown">
                  <button className="btn btn-outline-secondary dropdown-toggle" data-bs-toggle="dropdown">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="icon me-1">
                      <circle cx="12" cy="12" r="10"></circle>
                      <path d="M16 8h-6a2 2 0 1 0 0 4h4a2 2 0 1 1 0 4H8"></path>
                      <path d="M12 18V6"></path>
                    </svg>
                    {displayCurrency?.code || currency.code}
                  </button>
                  <div className="dropdown-menu">
                    {currencies.map((curr) => (
                      <button
                        key={curr.code}
                        className={`dropdown-item ${(displayCurrency?.code || currency.code) === curr.code ? 'active' : ''}`}
                        onClick={() => setDisplayCurrency({
                          code: curr.code,
                          name: curr.name,
                          symbol: curr.symbol,
                          exchange_rate: parseFloat(curr.exchange_rate)
                        })}
                      >
                        {curr.code} - {curr.name} ({curr.symbol})
                      </button>
                    ))}
                  </div>
                </div>
                <div className="dropdown">
                  <button className="btn btn-outline-primary dropdown-toggle" data-bs-toggle="dropdown">
                    {t(`contracts.timeRange.${timeRange}`)}
                  </button>
                  <div className="dropdown-menu">
                    <button
                      className={`dropdown-item ${timeRange === 'all' ? 'active' : ''}`}
                      onClick={() => setTimeRange('all')}
                    >
                      {t('contracts.timeRange.all')}
                    </button>
                    <button
                      className={`dropdown-item ${timeRange === 'year' ? 'active' : ''}`}
                      onClick={() => setTimeRange('year')}
                    >
                      {t('contracts.timeRange.year')}
                    </button>
                    <button
                      className={`dropdown-item ${timeRange === 'month' ? 'active' : ''}`}
                      onClick={() => setTimeRange('month')}
                    >
                      {t('contracts.timeRange.month')}
                    </button>
                    <button
                      className={`dropdown-item ${timeRange === 'week' ? 'active' : ''}`}
                      onClick={() => setTimeRange('week')}
                    >
                      {t('contracts.timeRange.week')}
                    </button>
                  </div>
                </div>
                <Link to="/contracts" className="btn btn-primary">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="icon me-1">
                    <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                    <path d="M5 12l14 0" />
                    <path d="M5 12l6 6" />
                    <path d="M5 12l6 -6" />
                  </svg>
                  {t('contracts.backToContracts')}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="page-body">
        <div className="container-xl">
          {error && (
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          )}

          {statistics && (
            <>
              {/* Overview Cards */}
              <div className="row row-deck row-cards mb-4">
                <div className="col-sm-6 col-lg-3">
                  <div className="card">
                    <div className="card-body">
                      <div className="d-flex align-items-center">
                        <div className="subheader">{t('contracts.totalContracts')}</div>
                        <div className="ms-auto">
                          <span className="text-primary">
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="icon">
                              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                              <polyline points="14,2 14,8 20,8"></polyline>
                              <line x1="16" y1="13" x2="8" y2="13"></line>
                              <line x1="16" y1="17" x2="8" y2="17"></line>
                              <polyline points="10,9 9,9 8,9"></polyline>
                            </svg>
                          </span>
                        </div>
                      </div>
                      <div className="h1 mb-3">{statistics.filteredContracts}</div>
                      <div className="d-flex mb-2">
                        <div>{t('contracts.activeContracts')}</div>
                        <div className="ms-auto">
                          <span className="text-success d-inline-flex align-items-center lh-1">
                            {statistics.active_contracts || 0}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-sm-6 col-lg-3">
                  <div className="card">
                    <div className="card-body">
                      <div className="d-flex align-items-center">
                        <div className="subheader">{t('contracts.totalValue')}</div>
                        <div className="ms-auto">
                          <span className="text-success">{displayCurrency?.symbol || currency.symbol}</span>
                        </div>
                      </div>
                      <div className="h1 mb-3">{formatCurrency(statistics.totalBudget, { currency: displayCurrency })}</div>
                      <div className="d-flex mb-2">
                        <div>{t('contracts.averageValue')}</div>
                        <div className="ms-auto">
                          <span className="text-muted">
                            {formatCurrency(statistics.avgBudget, { currency: displayCurrency })}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-sm-6 col-lg-3">
                  <div className="card">
                    <div className="card-body">
                      <div className="d-flex align-items-center">
                        <div className="subheader">{t('contracts.lockedContracts')}</div>
                        <div className="ms-auto">
                          <span className="text-warning">
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="icon">
                              <rect width="18" height="11" x="3" y="11" rx="2" ry="2"></rect>
                              <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                            </svg>
                          </span>
                        </div>
                      </div>
                      <div className="h1 mb-3">{statistics.lockedStats.locked}</div>
                      <div className="d-flex mb-2">
                        <div>{t('contracts.unlockedContracts')}</div>
                        <div className="ms-auto">
                          <span className="text-info d-inline-flex align-items-center lh-1">
                            {statistics.lockedStats.unlocked}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-sm-6 col-lg-3">
                  <div className="card">
                    <div className="card-body">
                      <div className="d-flex align-items-center">
                        <div className="subheader">{t('contracts.topCustomer')}</div>
                        <div className="ms-auto">
                          <span className="text-info">
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="icon">
                              <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                              <circle cx="9" cy="7" r="4"></circle>
                              <path d="M22 21v-2a4 4 0 0 0-3-3.87"></path>
                              <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                            </svg>
                          </span>
                        </div>
                      </div>
                      <div className="h1 mb-3">
                        {statistics.topCustomers[0]?.count || 0}
                      </div>
                      <div className="d-flex mb-2">
                        <div className="text-truncate">
                          {statistics.topCustomers[0]?.name || t('contracts.noCustomers')}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Charts and Details */}
              <div className="row row-deck row-cards">
                {/* Status Distribution */}
                <div className="col-lg-6">
                  <Card>
                    <div className="card-header">
                      <h3 className="card-title">{t('contracts.statusDistribution')}</h3>
                    </div>
                    <div className="card-body">
                      {Object.entries(statistics.statusDistribution).length > 0 ? (
                        <div className="space-y-2">
                          {Object.entries(statistics.statusDistribution).map(([status, count]) => {
                            const percentage = (count / statistics.filteredContracts) * 100;
                            return (
                              <div key={status} className="d-flex align-items-center mb-3">
                                <div className="me-3">
                                  <span className="badge" style={{ backgroundColor: getStatusHex(status), color: getContrastColor(getStatusHex(status)) }}>
                                    {status}
                                  </span>
                                </div>
                                <div className="flex-fill">
                                  <div className="progress progress-sm">
                                    <div
                                      className="progress-bar"
                                      style={{ width: `${percentage}%`, backgroundColor: getStatusHex(status) }}
                                    ></div>
                                  </div>
                                </div>
                                <div className="ms-3 text-muted">
                                  {count} ({percentage.toFixed(1)}%)
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="text-center py-4">
                          <p className="text-muted">{t('contracts.noStatusData')}</p>
                        </div>
                      )}
                    </div>
                  </Card>
                </div>

                {/* Top Customers */}
                <div className="col-lg-6">
                  <Card>
                    <div className="card-header">
                      <h3 className="card-title">{t('contracts.topCustomers')}</h3>
                    </div>
                    <div className="card-body">
                      {statistics.topCustomers.length > 0 ? (
                        <div className="table-responsive">
                          <table className="table table-sm">
                            <thead>
                              <tr>
                                <th>{t('contracts.customer')}</th>
                                <th>{t('contracts.contractCount')}</th>
                                <th>{t('contracts.totalValue')}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {statistics.topCustomers.map((customer, index) => (
                                <tr key={index}>
                                  <td>{customer.name}</td>
                                  <td>
                                    <span className="badge" style={{ backgroundColor: COLOR_PRIMARY, color: getContrastColor(COLOR_PRIMARY) }}>{customer.count}</span>
                                  </td>
                                  <td>{formatCurrency(customer.totalBudget, { currency: displayCurrency })}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div className="text-center py-4">
                          <p className="text-muted">{t('contracts.noCustomerData')}</p>
                        </div>
                      )}
                    </div>
                  </Card>
                </div>

                {/* Monthly Trend */}
                <div className="col-12">
                  <Card>
                    <div className="card-header">
                      <h3 className="card-title">{t('contracts.monthlyTrend')}</h3>
                    </div>
                    <div className="card-body">
                      <div className="d-flex flex-wrap gap-3">
                        {statistics.monthlyTrend.map((month, index) => (
                          <div key={index} className="text-center">
                            <div className="text-muted small">{month.month}</div>
                            <div className="h3 mb-0">{month.count}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </Card>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContractStatistics;
